import { APIError, apiRequest } from "@/lib/api";

export type BillingPlanFamily = "personal" | "enterprise" | "agent";
export type BillingWorkspaceType = "personal" | "organization";
export type BillingSubscriptionStatus =
  | "free"
  | "checkout_pending"
  | "active"
  | "past_due"
  | "cancel_at_period_end"
  | "canceled"
  | "expired"
  | "disputed"
  | string;

export type BillingAllowedAction =
  | "checkout"
  | "change_plan"
  | "cancel"
  | "resume"
  | "manage_payment_method"
  | "view_invoices"
  | "buy_topups"
  | string;

export type BillingCatalogPlan = {
  code: string;
  name: string;
  description: string;
  family: BillingPlanFamily;
  billing_scope: "workspace" | "agent" | string;
  cadence: "month" | string;
  currency: string;
  monthly_price_minor: number;
  price_display: string;
  included_compute_micros: string;
  included_compute_display: string;
  terms_version: string;
  effective_at: string;
  eligible: boolean;
  ineligibility_reason: string;
  seller_eligible: boolean;
  min_quantity: number;
  max_quantity: number;
};

export type BillingCatalog = {
  workspace_id: string;
  workspace_type: BillingWorkspaceType;
  catalog_version: string;
  terms_version: string;
  plans: BillingCatalogPlan[];
};

export type AgentSubscriptionUsage = {
  agent_id: string;
  agent_name: string;
  plan_code: string;
  subscription_id: string;
  subscription_item_id: string;
  status: string;
  period_start: string;
  period_end: string;
  used_percent: number;
  reserved_percent: number;
  available_percent: number;
  allowance_state: string;
  using_topups: boolean;
  topup_fallback_enabled: boolean;
};

export type SubscriptionUsage = {
  workspace_id: string;
  plan_code: string;
  period_start: string;
  period_end: string;
  used_percent: number;
  reserved_percent: number;
  available_percent: number;
  allowance_state: string;
  using_topups: boolean;
  agents: AgentSubscriptionUsage[];
};

export type BillingPlanSummary = {
  code: string;
  name: string;
  family: BillingPlanFamily | string;
  included_compute_micros: string;
  included_compute_display: string;
};

export type TopupUsage = {
  state: "none" | "active" | "exhausted" | "pending";
  remaining_percent: number | null;
  cycle_id: string;
};

export type WorkspaceBillingState = {
  workspace_id: string;
  workspace_type: BillingWorkspaceType;
  workspace_name: string;
  can_manage_billing: boolean;
  can_manage_agent_topup_fallback: boolean;
  billing_permission_reason: string;
  status: BillingSubscriptionStatus;
  plan: BillingPlanSummary;
  period_start: string;
  period_end: string;
  renews_at: string;
  access_ends_at: string;
  grace_ends_at: string;
  purchased_topup_micros: string;
  purchased_topup_display: string;
  topup_usage: TopupUsage;
  allowed_actions: BillingAllowedAction[];
  next_period_plan?: BillingPlanSummary;
  next_period_included_compute_micros: string;
  next_period_included_compute_display: string;
  next_period_effective_at: string;
  payment_method_summary: string;
};

export type BillingInvoice = {
  id: string;
  date: string;
  item_label: string;
  amount_minor: number;
  amount_display: string;
  currency: string;
  status: string;
  invoice_url: string;
};

export type BillingInvoicePage = {
  items: BillingInvoice[];
  next_cursor: string;
  has_more: boolean;
};

export type BillingOperationStatus = {
  workspace_id: string;
  operation_id: string;
  kind: string;
  status: "pending" | "succeeded" | "failed" | "expired";
  projected_billing_status: string;
  created_at: string;
  updated_at: string;
  error_code: string;
  error_message: string;
  next_action: string;
};

export type BillingCustomerAgent = {
  id: string;
  workspace_id: string;
  owner_user_id: string;
  name: string;
  status: string;
  subscription_status: string;
  created_at: string;
  updated_at: string;
};

export type BillingAgentCredential = {
  workspace_id: string;
  agent_id: string;
  agent_credential: string;
  credential_created_at: string;
};

export type BillingCreatedAgent = BillingCustomerAgent & BillingAgentCredential;

export type BillingActionResponseStatus =
  | "pending"
  | "succeeded"
  | "failed"
  | "expired"
  | "stripe_created"
  | "paid"
  | "credited";

export type BillingActionResponse = {
  workspace_id: string;
  operation_id: string;
  status: BillingActionResponseStatus;
  checkout_url: string;
  redirect_url: string;
  expires_at: string;
};

export type PlanChangePreview = {
  workspace_id: string;
  current_plan_code: string;
  target_plan_code: string;
  timing: "immediate_after_payment" | "next_period" | string;
  effective_at: string;
  amount_due_micros: string;
  amount_due_display: string;
  included_compute_micros: string;
  included_compute_display: string;
  currency: string;
  terms_version: string;
};

export type BillingErrorRecovery = {
  title: string;
  action: string;
  retryable: boolean;
  resetIntent?: boolean;
};

export type BillingIdempotencyRegistry = {
  keyFor: (intent: string) => string;
  clear: (intent: string) => void;
  clearAll: () => void;
};

const BILLING_ERROR_RECOVERY: Record<string, BillingErrorRecovery> = {
  ERR_SUBSCRIPTION_REQUIRED: {
    title: "This action needs a paid plan.",
    action: "Choose plan",
    retryable: false,
  },
  ERR_PLAN_SCOPE_INVALID: {
    title: "This plan is not available for the selected workspace.",
    action: "Choose an eligible plan",
    retryable: false,
  },
  ERR_BILLING_PERMISSION_REQUIRED: {
    title: "A workspace billing admin must make this change.",
    action: "Ask a billing admin",
    retryable: false,
  },
  ERR_SUBSCRIPTION_CHECKOUT_PENDING: {
    title: "Payment is still being confirmed.",
    action: "Refresh status",
    retryable: true,
  },
  ERR_NETWORK_ACCESS_REQUIRES_PAID_PLAN: {
    title: "Free accounts can use private provider routes only.",
    action: "Choose plan",
    retryable: false,
  },
  ERR_PROVIDER_PUBLISH_REQUIRES_PAID_PLAN: {
    title: "Publishing capacity requires a paid base plan.",
    action: "Choose plan",
    retryable: false,
  },
  ERR_AGENT_SUBSCRIPTION_REQUIRED: {
    title: "This agent needs a Power Your Agent subscription.",
    action: "Power this agent",
    retryable: false,
  },
  ERR_AGENT_ASSIGNMENT_INVALID: {
    title: "This agent cannot be assigned to the subscription.",
    action: "Choose another agent",
    retryable: false,
  },
  ERR_AGENT_SUBSCRIPTION_EXISTS: {
    title: "One or more selected agents already have a subscription.",
    action: "Choose another agent",
    retryable: false,
  },
  ERR_AGENT_SUBSCRIPTION_CHECKOUT_PENDING: {
    title: "A subscription checkout is already pending for this agent.",
    action: "Refresh status",
    retryable: true,
  },
  ERR_AGENT_NAME_INVALID: {
    title: "Enter an agent name between 1 and 120 characters.",
    action: "Edit agent name",
    retryable: false,
  },
  ERR_AGENT_OWNER_REQUIRED: {
    title: "Only the agent owner or workspace owner can rotate this credential.",
    action: "Ask the agent owner",
    retryable: false,
  },
  ERR_WORKSPACE_OWNER_REQUIRED: {
    title: "Only the workspace owner can change this setting.",
    action: "Ask the workspace owner",
    retryable: false,
  },
  ERR_AGENT_CREDENTIAL_ROTATION_CONFLICT: {
    title: "The credential changed concurrently. Retry the rotation.",
    action: "Retry",
    retryable: true,
  },
  ERR_AGENT_CREDENTIAL_ROTATION_SUPERSEDED: {
    title: "A newer credential rotation replaced this request.",
    action: "Rotate again",
    retryable: true,
    resetIntent: true,
  },
  ERR_SUBSCRIPTION_PAST_DUE: {
    title: "Payment needs attention.",
    action: "Fix payment",
    retryable: false,
  },
  ERR_INSUFFICIENT_CREDITS: {
    title: "The plan is active, but there are not enough credits.",
    action: "Buy extra credits",
    retryable: false,
  },
  ERR_BILLING_TERMS_STALE: {
    title: "The plan terms changed before confirmation.",
    action: "Review updated terms",
    retryable: true,
  },
  ERR_PAYMENT_NOT_COMPLETED: {
    title: "Stripe has not confirmed this payment.",
    action: "Check payment status",
    retryable: true,
  },
  ERR_PAYMENT_MANAGEMENT_UNAVAILABLE: {
    title: "Payment management is not available for this workspace yet.",
    action: "Contact support",
    retryable: false,
  },
  ERR_PLAN_ALREADY_SELECTED: {
    title: "This plan is already selected.",
    action: "Refresh status",
    retryable: false,
  },
  ERR_BILLING_WORKSPACE_INACTIVE: {
    title: "Billing is unavailable for this inactive workspace.",
    action: "Switch workspace",
    retryable: false,
  },
  ERR_IDEMPOTENCY_KEY_REUSED: {
    title: "This billing request conflicts with an earlier action.",
    action: "Start again",
    retryable: false,
    resetIntent: true,
  },
  ERR_STRIPE_NOT_CONFIGURED: {
    title: "Subscription checkout is not available right now.",
    action: "Contact support",
    retryable: false,
    resetIntent: true,
  },
  ERR_STRIPE_PRICE_INVALID: {
    title: "This plan is not configured for checkout.",
    action: "Choose another plan",
    retryable: false,
    resetIntent: true,
  },
  ERR_STRIPE_PRICE_VALIDATION_RETRY: {
    title: "Stripe price validation is temporarily unavailable.",
    action: "Try again",
    retryable: true,
  },
  ERR_STRIPE_CHECKOUT_CREATE_FAILED: {
    title: "Stripe checkout could not be started.",
    action: "Try again",
    retryable: true,
    resetIntent: true,
  },
  ERR_STRIPE_CHECKOUT_RETRYABLE: {
    title: "Stripe checkout is temporarily unavailable.",
    action: "Try again",
    retryable: true,
  },
  ERR_STRIPE_CHECKOUT_RETRY: {
    title: "Stripe checkout is temporarily unavailable.",
    action: "Try again",
    retryable: true,
  },
  ERR_BILLING_CHECKOUT_FAILED: {
    title: "Subscription checkout could not be started.",
    action: "Try again",
    retryable: true,
    resetIntent: true,
  },
  ERR_BILLING_CHANGE_FAILED: {
    title: "The subscription change could not be completed.",
    action: "Review plan",
    retryable: true,
    resetIntent: true,
  },
  ERR_BILLING_ACTION_FAILED: {
    title: "The subscription action could not be completed.",
    action: "Refresh status",
    retryable: true,
    resetIntent: true,
  },
};

function workspaceBillingPath(workspaceId: string, suffix = "") {
  const id = String(workspaceId || "").trim();
  if (!id) throw new Error("A workspace must be selected before loading billing.");
  return `/api/v1/workspaces/${encodeURIComponent(id)}/billing${suffix}`;
}

function billingIdempotencyHeaders(idempotencyKey: string) {
  const key = String(idempotencyKey || "").trim();
  if (!key) throw new Error("An idempotency key is required for this billing action.");
  return { "Idempotency-Key": key };
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function stringValue(value: unknown, fallback = "") {
  return value === undefined || value === null ? fallback : String(value);
}

function numberValue(value: unknown, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function booleanValue(value: unknown, fallback = false) {
  if (typeof value === "boolean") return value;
  if (value === "true" || value === 1 || value === "1") return true;
  if (value === "false" || value === 0 || value === "0") return false;
  return fallback;
}

function familyValue(value: unknown): BillingPlanFamily {
  const family = stringValue(value).toLowerCase();
  if (family === "enterprise" || family === "agent") return family;
  return "personal";
}

function isBillingPlanFamily(value: string): value is BillingPlanFamily {
  return value === "personal" || value === "enterprise" || value === "agent";
}

function workspaceTypeValue(value: unknown): BillingWorkspaceType {
  const type = stringValue(value).toLowerCase();
  return type === "organization" || type === "enterprise" || type === "org" || type === "team"
    ? "organization"
    : "personal";
}

function formatMoneyFromMinor(amount: number, currency: string) {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(amount / 100);
  } catch {
    return `${currency || "USD"} ${(amount / 100).toFixed(2)}`;
  }
}

export function normalizeBillingCatalogPlan(value: unknown): BillingCatalogPlan {
  const raw = objectValue(value);
  const code = stringValue(raw.code ?? raw.plan_code);
  const rawFamily = stringValue(raw.family).toLowerCase();
  const family = familyValue(raw.family);
  const monthlyPriceMinor = numberValue(
    raw.monthly_price_minor ??
      raw.monthly_price_cents ??
      raw.price_minor ??
      raw.price_cents ??
      numberValue(raw.monthly_price_micros ?? raw.price_micros) / 10_000,
  );
  const currency = stringValue(raw.currency, "USD").toUpperCase();
  const billingScope = stringValue(raw.billing_scope ?? raw.scope, "workspace");
  const cadence = stringValue(raw.cadence ?? raw.billing_interval ?? raw.interval, "month");
  const minimumQuantity = Math.max(
    1,
    Math.trunc(numberValue(raw.min_quantity ?? raw.minimum_quantity, 1)),
  );
  const maximumQuantity = Math.max(
    minimumQuantity,
    Math.trunc(numberValue(raw.max_quantity ?? raw.maximum_quantity, family === "agent" ? 10 : 1)),
  );
  const shapeEligible =
    isBillingPlanFamily(rawFamily) &&
    cadence === "month" &&
    (code === "free" || (Number.isInteger(monthlyPriceMinor) && monthlyPriceMinor > 0)) &&
    ((family === "agent" && billingScope === "agent") ||
      (family !== "agent" && billingScope === "workspace"));
  const serverEligible = raw.eligible === undefined ? true : booleanValue(raw.eligible);
  const eligible = serverEligible && shapeEligible;
  return {
    code,
    name: stringValue(raw.name ?? raw.display_name),
    description: stringValue(raw.description),
    family,
    billing_scope: billingScope,
    cadence,
    currency,
    monthly_price_minor: monthlyPriceMinor,
    price_display: stringValue(
      raw.price_display ?? raw.monthly_price_display,
      formatMoneyFromMinor(monthlyPriceMinor, currency),
    ),
    included_compute_micros: stringValue(
      raw.included_compute_micros ?? raw.included_credit_micros,
      "0",
    ),
    included_compute_display: stringValue(
      raw.included_compute_display ?? raw.included_credit_display ?? raw.included_display,
    ),
    terms_version: stringValue(raw.terms_version),
    effective_at: stringValue(raw.effective_at),
    eligible,
    ineligibility_reason: stringValue(
      raw.ineligibility_reason ?? raw.eligibility_reason,
      eligible
        ? ""
        : shapeEligible
          ? "This plan is not available for the selected workspace."
          : "This plan has invalid billing terms and cannot be selected.",
    ),
    seller_eligible: booleanValue(raw.seller_eligible, family !== "agent"),
    min_quantity: minimumQuantity,
    max_quantity: maximumQuantity,
  };
}

export function normalizeBillingCatalog(value: unknown): BillingCatalog {
  const envelope = objectValue(value);
  const raw = objectValue(envelope.catalog ?? envelope);
  const plans = Array.isArray(raw.plans ?? raw.items)
    ? ((raw.plans ?? raw.items) as unknown[])
    : [];
  return {
    workspace_id: stringValue(raw.workspace_id ?? envelope.workspace_id),
    workspace_type: workspaceTypeValue(raw.workspace_type),
    catalog_version: stringValue(raw.catalog_version ?? raw.version),
    terms_version: stringValue(raw.terms_version),
    plans: plans.map(normalizeBillingCatalogPlan).filter((plan) => Boolean(plan.code)),
  };
}

function normalizePlanSummary(value: unknown, fallbackCode = "free"): BillingPlanSummary {
  const raw = objectValue(value);
  const code = stringValue(raw.code ?? raw.plan_code, fallbackCode);
  return {
    code,
    name: stringValue(raw.name ?? raw.display_name, code === "free" ? "Free" : code),
    family: stringValue(raw.family, "personal"),
    included_compute_micros: stringValue(raw.included_compute_micros, "0"),
    included_compute_display: stringValue(raw.included_compute_display),
  };
}

function normalizeTopupUsage(value: unknown): TopupUsage {
  const raw = objectValue(value);
  const rawState = stringValue(raw.state).toLowerCase();
  const validState =
    rawState === "none" ||
    rawState === "active" ||
    rawState === "exhausted" ||
    rawState === "pending";
  const state: TopupUsage["state"] = validState ? (rawState as TopupUsage["state"]) : "pending";
  const cycleId = stringValue(raw.cycle_id);
  const rawPercent = raw.remaining_percent;
  const parsedPercent =
    typeof rawPercent === "number" || (typeof rawPercent === "string" && rawPercent.trim() !== "")
      ? Number(rawPercent)
      : null;
  const remainingPercent =
    parsedPercent !== null &&
    Number.isFinite(parsedPercent) &&
    parsedPercent >= 0 &&
    parsedPercent <= 100
      ? parsedPercent
      : null;

  if (
    !validState ||
    state === "pending" ||
    ((state === "active" || state === "exhausted") && cycleId === "") ||
    (state === "active" && remainingPercent === null)
  ) {
    return {
      state: "pending",
      remaining_percent: null,
      cycle_id: cycleId,
    };
  }

  return {
    state,
    remaining_percent: state === "none" || state === "exhausted" ? 0 : remainingPercent,
    cycle_id: cycleId,
  };
}

export function normalizeWorkspaceBillingState(value: unknown): WorkspaceBillingState {
  const envelope = objectValue(value);
  const raw = objectValue(envelope.billing ?? envelope.state ?? envelope);
  const rawStatus = stringValue(
    raw.status ?? raw.subscription_status ?? raw.billing_status,
    "free",
  );
  const status =
    booleanValue(raw.cancel_at_period_end) && rawStatus === "active"
      ? "cancel_at_period_end"
      : rawStatus;
  const plan = normalizePlanSummary(
    raw.plan ??
      raw.current_plan ?? {
        plan_code: raw.plan_code,
        display_name: raw.plan_name,
        family: raw.plan_family,
        included_compute_micros: raw.included_compute_micros,
        included_compute_display: raw.included_compute_display,
      },
  );
  const allowedActions = Array.isArray(raw.allowed_actions)
    ? raw.allowed_actions.map((action) => stringValue(action)).filter(Boolean)
    : [];
  return {
    workspace_id: stringValue(raw.workspace_id),
    workspace_type: workspaceTypeValue(raw.workspace_type),
    workspace_name: stringValue(raw.workspace_name),
    can_manage_billing: booleanValue(raw.can_manage_billing ?? raw.billing_permission),
    can_manage_agent_topup_fallback: booleanValue(raw.can_manage_agent_topup_fallback),
    billing_permission_reason: stringValue(raw.billing_permission_reason),
    status,
    plan,
    period_start: stringValue(raw.period_start ?? raw.current_period_start),
    period_end: stringValue(raw.period_end ?? raw.current_period_end),
    renews_at: stringValue(raw.renews_at ?? raw.current_period_end),
    access_ends_at: stringValue(raw.access_ends_at),
    grace_ends_at: stringValue(raw.grace_ends_at),
    purchased_topup_micros: stringValue(
      raw.purchased_topup_micros ?? raw.purchased_topup_balance_micros,
      "0",
    ),
    purchased_topup_display: stringValue(
      raw.purchased_topup_display ?? raw.purchased_topup_balance_display,
      "0 credits",
    ),
    topup_usage: normalizeTopupUsage(raw.topup_usage),
    allowed_actions: allowedActions,
    next_period_plan: raw.next_period_plan
      ? normalizePlanSummary(raw.next_period_plan, plan.code)
      : undefined,
    next_period_included_compute_micros: stringValue(raw.next_period_included_compute_micros),
    next_period_included_compute_display: stringValue(raw.next_period_included_compute_display),
    next_period_effective_at: stringValue(raw.next_period_effective_at ?? raw.effective_at),
    payment_method_summary: stringValue(raw.payment_method_summary),
  };
}

export function normalizeSubscriptionUsage(value: unknown): SubscriptionUsage {
  const envelope = objectValue(value);
  const raw = objectValue(envelope.usage ?? envelope);
  const agents = Array.isArray(raw.agents) ? raw.agents : [];
  const usedPercent = percentageValue(raw.used_percent);
  const reservedPercent = percentageValue(raw.reserved_percent);
  const availablePercent = percentageValue(raw.available_percent);
  return {
    workspace_id: stringValue(raw.workspace_id),
    plan_code: stringValue(raw.plan_code),
    period_start: stringValue(raw.period_start),
    period_end: stringValue(raw.period_end),
    used_percent: usedPercent,
    reserved_percent: reservedPercent,
    available_percent: availablePercent,
    allowance_state: stringValue(raw.allowance_state, "free"),
    using_topups: booleanValue(raw.using_topups),
    agents: agents
      .map((value): AgentSubscriptionUsage => {
        const agent = objectValue(value);
        const agentUsedPercent = percentageValue(agent.used_percent);
        const agentReservedPercent = percentageValue(agent.reserved_percent);
        return {
          agent_id: stringValue(agent.agent_id),
          agent_name: stringValue(agent.agent_name),
          plan_code: stringValue(agent.plan_code, "agent_power"),
          subscription_id: stringValue(agent.subscription_id),
          subscription_item_id: stringValue(agent.subscription_item_id),
          status: stringValue(agent.status),
          period_start: stringValue(agent.period_start),
          period_end: stringValue(agent.period_end),
          used_percent: agentUsedPercent,
          reserved_percent: agentReservedPercent,
          available_percent: percentageValue(agent.available_percent),
          allowance_state: stringValue(agent.allowance_state, "active"),
          using_topups: booleanValue(agent.using_topups),
          topup_fallback_enabled: booleanValue(agent.topup_fallback_enabled),
        };
      })
      .filter((agent) => Boolean(agent.agent_id)),
  };
}

function normalizeInvoice(value: unknown): BillingInvoice {
  const raw = objectValue(value);
  return {
    id: stringValue(raw.id ?? raw.invoice_id),
    date: stringValue(raw.date ?? raw.created_at),
    item_label: stringValue(raw.item_label ?? raw.description ?? raw.plan_name),
    amount_minor: numberValue(raw.amount_minor ?? raw.amount_cents),
    amount_display: stringValue(raw.amount_display ?? raw.amount_paid_display),
    currency: stringValue(raw.currency, "USD"),
    status: stringValue(raw.status),
    invoice_url: stringValue(raw.invoice_url ?? raw.document_url),
  };
}

export function normalizeBillingInvoicePage(value: unknown): BillingInvoicePage {
  const envelope = objectValue(value);
  const raw = objectValue(envelope);
  const page = objectValue(raw.page);
  const items = Array.isArray(raw.items)
    ? raw.items
    : Array.isArray(raw.invoices)
      ? raw.invoices
      : [];
  return {
    items: items.map(normalizeInvoice),
    next_cursor: stringValue(raw.next_cursor ?? page.next_cursor),
    has_more: booleanValue(raw.has_more ?? page.has_more),
  };
}

export function normalizeBillingOperation(value: unknown): BillingOperationStatus {
  const envelope = objectValue(value);
  const raw = objectValue(envelope.operation ?? envelope);
  return {
    workspace_id: stringValue(raw.workspace_id ?? envelope.workspace_id),
    operation_id: stringValue(raw.operation_id ?? raw.id),
    kind: stringValue(raw.kind ?? raw.operation),
    status: stringValue(raw.status).toLowerCase() as BillingOperationStatus["status"],
    projected_billing_status: stringValue(raw.projected_billing_status),
    created_at: stringValue(raw.created_at),
    updated_at: stringValue(raw.updated_at),
    error_code: stringValue(raw.error_code),
    error_message: stringValue(raw.error_message ?? raw.error),
    next_action: stringValue(raw.next_action),
  };
}

export function normalizePlanChangePreview(value: unknown): PlanChangePreview {
  const envelope = objectValue(value);
  const raw = objectValue(envelope.preview ?? envelope);
  return {
    workspace_id: stringValue(raw.workspace_id ?? envelope.workspace_id),
    current_plan_code: stringValue(raw.current_plan_code),
    target_plan_code: stringValue(raw.target_plan_code),
    timing: stringValue(raw.timing),
    effective_at: stringValue(raw.effective_at),
    amount_due_micros: stringValue(raw.amount_due_micros),
    amount_due_display: stringValue(raw.amount_due_display),
    included_compute_micros: stringValue(raw.included_compute_micros),
    included_compute_display: stringValue(raw.included_compute_display),
    currency: stringValue(raw.currency),
    terms_version: stringValue(raw.terms_version),
  };
}

export function validatePlanChangePreview(
  preview: PlanChangePreview,
  expected: {
    workspaceId: string;
    currentPlanCode?: string;
    targetPlanCode: string;
    termsVersion: string;
  },
) {
  const exactFields = [
    [preview.workspace_id, expected.workspaceId, "workspace"],
    [preview.target_plan_code, expected.targetPlanCode, "target plan"],
    [preview.terms_version, expected.termsVersion, "terms"],
  ] as const;
  for (const [actual, wanted, label] of exactFields) {
    if (!actual || actual !== wanted) {
      throw new Error(`The plan-change preview ${label} did not match the requested change.`);
    }
  }
  if (
    !preview.current_plan_code ||
    (expected.currentPlanCode && preview.current_plan_code !== expected.currentPlanCode)
  ) {
    throw new Error("The plan-change preview current plan did not match the selected workspace.");
  }
  if (preview.timing !== "immediate_after_payment" && preview.timing !== "next_period") {
    throw new Error("The plan-change preview did not include valid billing timing.");
  }
  if (!Number.isFinite(Date.parse(preview.effective_at))) {
    throw new Error("The plan-change preview did not include a valid effective date.");
  }
  if (
    !isNonNegativeIntegerString(preview.amount_due_micros) ||
    !preview.amount_due_display.trim() ||
    !isPositiveIntegerString(preview.included_compute_micros) ||
    !preview.included_compute_display.trim() ||
    !/^[A-Z]{3}$/.test(preview.currency)
  ) {
    throw new Error("The plan-change preview did not include valid monetary terms.");
  }
  return preview;
}

function normalizeActionResponse(value: unknown): BillingActionResponse {
  const envelope = objectValue(value);
  const raw = objectValue(envelope.action ?? envelope.checkout ?? envelope);
  const status = stringValue(raw.status).trim().toLowerCase();
  if (!isBillingActionResponseStatus(status)) {
    throw new Error("The billing action response did not include a valid status.");
  }
  return {
    workspace_id: stringValue(raw.workspace_id ?? envelope.workspace_id),
    operation_id: stringValue(raw.operation_id),
    status,
    checkout_url: stringValue(raw.checkout_url ?? raw.portal_url ?? raw.url),
    redirect_url: stringValue(raw.redirect_url ?? raw.checkout_url ?? raw.portal_url ?? raw.url),
    expires_at: stringValue(raw.expires_at),
  };
}

function isBillingActionResponseStatus(value: string): value is BillingActionResponseStatus {
  return (
    value === "pending" ||
    value === "succeeded" ||
    value === "failed" ||
    value === "expired" ||
    value === "stripe_created" ||
    value === "paid" ||
    value === "credited"
  );
}

export function readBillingCatalog(workspaceId: string, signal?: AbortSignal) {
  return apiRequest<unknown>(workspaceBillingPath(workspaceId, "/catalog"), {
    method: "GET",
    signal,
    cache: "no-store",
  }).then((value) => {
    const catalog = normalizeBillingCatalog(value);
    assertWorkspaceResponse(workspaceId, catalog.workspace_id, "billing catalogue");
    return catalog;
  });
}

export function readWorkspaceBilling(workspaceId: string, signal?: AbortSignal) {
  return apiRequest<unknown>(workspaceBillingPath(workspaceId), {
    method: "GET",
    signal,
    cache: "no-store",
  }).then((value) => {
    const state = normalizeWorkspaceBillingState(value);
    assertWorkspaceResponse(workspaceId, state.workspace_id, "billing state");
    return state;
  });
}

export function readSubscriptionUsage(workspaceId: string, signal?: AbortSignal) {
  return apiRequest<unknown>(workspaceBillingPath(workspaceId, "/usage"), {
    method: "GET",
    signal,
    cache: "no-store",
  }).then((value) => {
    const usage = normalizeSubscriptionUsage(value);
    assertWorkspaceResponse(workspaceId, usage.workspace_id, "subscription usage");
    return usage;
  });
}

export function readBillingInvoices(workspaceId: string, cursor = "", signal?: AbortSignal) {
  const suffix = cursor ? `/invoices?cursor=${encodeURIComponent(cursor)}` : "/invoices";
  return apiRequest<unknown>(workspaceBillingPath(workspaceId, suffix), {
    method: "GET",
    signal,
    cache: "no-store",
  }).then((value) => {
    const raw = objectValue(value);
    assertWorkspaceResponse(workspaceId, stringValue(raw.workspace_id), "billing invoices");
    return normalizeBillingInvoicePage(value);
  });
}

export function readBillingOperation(
  workspaceId: string,
  operationId: string,
  signal?: AbortSignal,
) {
  const operation = String(operationId || "").trim();
  if (!operation) throw new Error("A billing operation ID is required.");
  return apiRequest<unknown>(
    workspaceBillingPath(workspaceId, `/operations/${encodeURIComponent(operation)}`),
    { method: "GET", signal, cache: "no-store" },
  ).then((value) => {
    const result = normalizeBillingOperation(value);
    assertWorkspaceResponse(workspaceId, result.workspace_id, "billing operation");
    if (result.operation_id !== operation) {
      throw new Error("The billing operation response did not match the requested operation.");
    }
    if (!["pending", "succeeded", "failed", "expired"].includes(result.status)) {
      throw new Error("The billing operation response included an invalid status.");
    }
    return result;
  });
}

export function createSubscriptionCheckout(
  workspaceId: string,
  input: {
    plan_code: string;
    expected_terms_version: string;
    return_path: string;
    agent_ids?: string[];
    quantity?: number;
  },
  idempotencyKey: string,
) {
  return apiRequest<unknown>(workspaceBillingPath(workspaceId, "/checkout"), {
    method: "POST",
    body: input,
    headers: billingIdempotencyHeaders(idempotencyKey),
    cache: "no-store",
  }).then((value) => normalizeWorkspaceActionResponse(value, workspaceId, "subscription checkout"));
}

function normalizeBillingCustomerAgent(value: unknown): BillingCustomerAgent {
  const raw = objectValue(value);
  return {
    id: stringValue(raw.id),
    workspace_id: stringValue(raw.workspace_id),
    owner_user_id: stringValue(raw.owner_user_id),
    name: stringValue(raw.name),
    status: stringValue(raw.status),
    subscription_status: stringValue(raw.subscription_status, "unsubscribed"),
    created_at: stringValue(raw.created_at),
    updated_at: stringValue(raw.updated_at),
  };
}

export function readBillingAgents(workspaceId: string, signal?: AbortSignal) {
  return apiRequest<unknown>(workspaceBillingPath(workspaceId, "/agents"), {
    method: "GET",
    signal,
    cache: "no-store",
  }).then((value) => {
    const raw = objectValue(value);
    assertWorkspaceResponse(workspaceId, stringValue(raw.workspace_id), "billing agents");
    const agents = Array.isArray(raw.agents) ? raw.agents : [];
    return agents.map((value) => {
      const agent = normalizeBillingCustomerAgent(value);
      if (!agent.id) throw new Error("The billing agent response included an invalid agent.");
      assertWorkspaceResponse(workspaceId, agent.workspace_id, "billing agent");
      return agent;
    });
  });
}

export function createBillingAgent(workspaceId: string, name: string, idempotencyKey: string) {
  return apiRequest<unknown>(workspaceBillingPath(workspaceId, "/agents"), {
    method: "POST",
    body: { name },
    headers: billingIdempotencyHeaders(idempotencyKey),
    cache: "no-store",
  }).then((value): BillingCreatedAgent => {
    const raw = objectValue(value);
    const agent = normalizeBillingCustomerAgent(raw.agent ?? value);
    const result = {
      ...agent,
      workspace_id: agent.workspace_id || stringValue(raw.workspace_id),
      agent_id: stringValue(raw.agent_id, agent.id),
      agent_credential: stringValue(raw.agent_credential),
      credential_created_at: stringValue(raw.credential_created_at),
    };
    if (!result.id) throw new Error("The server did not return the created agent.");
    assertWorkspaceResponse(workspaceId, result.workspace_id, "created agent");
    return result;
  });
}

export function rotateBillingAgentCredential(
  workspaceId: string,
  agentId: string,
  idempotencyKey: string,
) {
  const agent = String(agentId || "").trim();
  if (!agent) throw new Error("An agent must be selected before rotating its credential.");
  return apiRequest<unknown>(
    workspaceBillingPath(workspaceId, `/agents/${encodeURIComponent(agent)}/credential`),
    {
      method: "POST",
      body: {},
      headers: billingIdempotencyHeaders(idempotencyKey),
      cache: "no-store",
    },
  ).then((value): BillingAgentCredential => {
    const raw = objectValue(value);
    const result = {
      workspace_id: stringValue(raw.workspace_id),
      agent_id: stringValue(raw.agent_id, agent),
      agent_credential: stringValue(raw.agent_credential),
      credential_created_at: stringValue(raw.credential_created_at),
    };
    assertWorkspaceResponse(workspaceId, result.workspace_id, "agent credential");
    if (result.agent_id !== agent || !result.agent_credential) {
      throw new Error("The server did not return the requested one-time agent credential.");
    }
    return result;
  });
}

export function updateAgentTopupFallback(workspaceId: string, agentId: string, enabled: boolean) {
  const agent = String(agentId || "").trim();
  if (!agent) throw new Error("An agent must be selected before updating credit fallback.");
  return apiRequest<unknown>(
    workspaceBillingPath(workspaceId, `/agents/${encodeURIComponent(agent)}/topup-fallback`),
    {
      method: "PATCH",
      body: { enabled },
      cache: "no-store",
    },
  ).then((value) => {
    const raw = objectValue(value);
    assertWorkspaceResponse(workspaceId, stringValue(raw.workspace_id), "agent credit fallback");
    if (stringValue(raw.agent_id) !== agent) {
      throw new Error("The agent credit fallback response did not match the selected agent.");
    }
    return value;
  });
}

export function createWorkspaceTopupCheckout(
  workspaceId: string,
  input: {
    credit_amount_micros: number;
    return_path: string;
  },
  idempotencyKey: string,
) {
  return apiRequest<unknown>(workspaceBillingPath(workspaceId, "/topups/checkout"), {
    method: "POST",
    body: input,
    headers: billingIdempotencyHeaders(idempotencyKey),
    cache: "no-store",
  }).then((value) => normalizeWorkspaceActionResponse(value, workspaceId, "top-up checkout"));
}

export function changeSubscriptionPlan(
  workspaceId: string,
  input: {
    plan_code: string;
    expected_terms_version: string;
    return_path: string;
  },
  idempotencyKey: string,
) {
  return apiRequest<unknown>(workspaceBillingPath(workspaceId, "/change-plan"), {
    method: "POST",
    body: input,
    headers: billingIdempotencyHeaders(idempotencyKey),
    cache: "no-store",
  }).then((value) => normalizeWorkspaceActionResponse(value, workspaceId, "plan change"));
}

export function previewSubscriptionPlanChange(
  workspaceId: string,
  input: {
    plan_code: string;
    expected_terms_version: string;
  },
  signal?: AbortSignal,
) {
  return apiRequest<unknown>(workspaceBillingPath(workspaceId, "/change-plan/preview"), {
    method: "POST",
    body: input,
    signal,
    cache: "no-store",
  }).then((value) =>
    validatePlanChangePreview(normalizePlanChangePreview(value), {
      workspaceId,
      targetPlanCode: input.plan_code,
      termsVersion: input.expected_terms_version,
    }),
  );
}

export function cancelSubscription(workspaceId: string, idempotencyKey: string) {
  return apiRequest<unknown>(workspaceBillingPath(workspaceId, "/cancel"), {
    method: "POST",
    body: { at_period_end: true },
    headers: billingIdempotencyHeaders(idempotencyKey),
    cache: "no-store",
  }).then((value) =>
    normalizeWorkspaceActionResponse(value, workspaceId, "subscription cancellation"),
  );
}

export function resumeSubscription(workspaceId: string, idempotencyKey: string) {
  return apiRequest<unknown>(workspaceBillingPath(workspaceId, "/resume"), {
    method: "POST",
    body: {},
    headers: billingIdempotencyHeaders(idempotencyKey),
    cache: "no-store",
  }).then((value) =>
    normalizeWorkspaceActionResponse(value, workspaceId, "subscription resumption"),
  );
}

export function createBillingPortalSession(
  workspaceId: string,
  returnPath: string,
  idempotencyKey: string,
) {
  return apiRequest<unknown>(workspaceBillingPath(workspaceId, "/portal"), {
    method: "POST",
    body: { return_path: returnPath },
    headers: billingIdempotencyHeaders(idempotencyKey),
    cache: "no-store",
  }).then((value) => normalizeWorkspaceActionResponse(value, workspaceId, "billing portal"));
}

export function createBillingIdempotencyKey(prefix = "billing") {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return `${prefix}:${uuid}`;
  return `${prefix}:${Date.now()}:${Math.random().toString(36).slice(2)}`;
}

export function createBillingIdempotencyRegistry(
  createKey: (prefix?: string) => string = createBillingIdempotencyKey,
): BillingIdempotencyRegistry {
  const keys = new Map<string, string>();
  return {
    keyFor(intent) {
      const normalizedIntent = String(intent || "").trim();
      if (!normalizedIntent) throw new Error("A billing intent is required.");
      const existing = keys.get(normalizedIntent);
      if (existing) return existing;
      const key = createKey(normalizedIntent);
      keys.set(normalizedIntent, key);
      return key;
    },
    clear(intent) {
      keys.delete(String(intent || "").trim());
    },
    clearAll() {
      keys.clear();
    },
  };
}

export function billingErrorRecovery(error: unknown): BillingErrorRecovery {
  const code = error instanceof APIError ? error.code : stringValue(objectValue(error).code);
  const known = BILLING_ERROR_RECOVERY[code];
  if (known) return known;
  const status = error instanceof APIError ? error.status : 0;
  const retryable =
    !status ||
    status === 408 ||
    status === 409 ||
    status === 425 ||
    status === 429 ||
    status >= 500;
  return {
    title:
      error instanceof Error && error.message ? error.message : "Billing could not be updated.",
    action: retryable ? "Try again" : "Review request",
    retryable,
  };
}

export function canPerformBillingAction(
  state: WorkspaceBillingState | null | undefined,
  action: BillingAllowedAction,
) {
  const aliases: Record<string, string[]> = {
    checkout: ["checkout", "choose_plan"],
    change_plan: ["change_plan"],
    cancel: ["cancel"],
    resume: ["resume"],
    manage_payment_method: ["manage_payment_method", "manage_payment"],
    view_invoices: ["view_invoices"],
    buy_topups: ["buy_topups", "buy_credits"],
  };
  return Boolean(
    state?.can_manage_billing &&
    Array.isArray(state.allowed_actions) &&
    (aliases[action] || [action]).some((candidate) => state.allowed_actions.includes(candidate)),
  );
}

export function billingHasPaidNetworkAccess(
  state: WorkspaceBillingState | null | undefined,
  now = Date.now(),
) {
  if (!state || state.plan.code === "free") return false;
  const status = String(state.status || "").toLowerCase();
  if (status === "active" || status === "cancel_at_period_end") {
    const accessEndsAt = Date.parse(state.access_ends_at || state.period_end || "");
    return Number.isFinite(accessEndsAt) && accessEndsAt > now;
  }
  if (status !== "past_due" && status !== "past_due_grace") return false;
  const graceEndsAt = Date.parse(state.grace_ends_at || "");
  return Number.isFinite(graceEndsAt) && graceEndsAt > now;
}

export function planEligibleForWorkspace(
  plan: BillingCatalogPlan,
  workspaceType: BillingWorkspaceType,
) {
  if (!plan.eligible) return false;
  if (
    !plan.code ||
    !plan.terms_version ||
    plan.cadence !== "month" ||
    !Number.isFinite(plan.monthly_price_minor) ||
    !Number.isInteger(plan.monthly_price_minor) ||
    (plan.code === "free" ? plan.monthly_price_minor < 0 : plan.monthly_price_minor <= 0)
  )
    return false;
  if (plan.family === "agent" && plan.billing_scope !== "agent") return false;
  if (plan.family !== "agent" && plan.billing_scope !== "workspace") return false;
  if (plan.family === "enterprise")
    return workspaceType === "personal" || workspaceType === "organization";
  if (plan.family === "personal") return workspaceType === "personal";
  return true;
}

function assertWorkspaceResponse(
  requestedWorkspaceId: string,
  responseWorkspaceId: string,
  resource: string,
) {
  const requested = String(requestedWorkspaceId || "").trim();
  const response = String(responseWorkspaceId || "").trim();
  if (!requested || response !== requested) {
    throw new Error(`The ${resource} response did not match the selected workspace.`);
  }
}

function percentageValue(value: unknown) {
  if (value === undefined || value === null || value === "") return Number.NaN;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : Number.NaN;
}

function isNonNegativeIntegerString(value: string) {
  return /^(0|[1-9]\d*)$/.test(value);
}

function isPositiveIntegerString(value: string) {
  return /^[1-9]\d*$/.test(value);
}

function normalizeWorkspaceActionResponse(value: unknown, workspaceId: string, resource: string) {
  const response = normalizeActionResponse(value);
  assertWorkspaceResponse(workspaceId, response.workspace_id, resource);
  return response;
}
